import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * The box every page-like view (Status, Awaiting, My tasks, Boards, Apps, Activity,
 * Projects, Settings) sits in, so they share one width and one gutter. Pages used to
 * pick their own max-width, and on a wide window the text began 100px in on one page
 * and 280px in on the next. The gutter follows the window, not the screen (`win-*`),
 * and matches the board's, so moving between a board and a page does not shift the edge.
 */
export const PAGE_CLASS = "mx-auto w-full max-w-5xl px-4 pb-8 pt-4 win-sm:px-6 win-sm:pt-6";

export const Page = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn(PAGE_CLASS, className)} {...props} />
));
Page.displayName = "Page";

/**
 * A page's title, the line under it and its actions, the same size on every page.
 * With the tab layout the top bar already names the view, so `showTitle` false drops
 * the title, and the description shows only from `win-sm` up, where there is room.
 */
export function PageHeader({
  title,
  description,
  actions,
  showTitle = true,
  className,
}: {
  title: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  showTitle?: boolean;
  className?: string;
}) {
  if (!showTitle && !description) return null;
  return (
    <div className={cn("mb-5 flex items-start gap-3", !showTitle && "hidden win-sm:flex", className)}>
      <div className="min-w-0 flex-1">
        {showTitle ? <h1 className="text-lg font-semibold leading-tight tracking-tight">{title}</h1> : null}
        {description ? <p className={cn("text-[13px] text-muted-foreground", showTitle && "mt-1")}>{description}</p> : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </div>
  );
}
